import {
  alpha,
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { red } from '@mui/material/colors';
import clsx from 'clsx';
import { useRecoilValueLoadable } from 'recoil';
import {
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';

const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    padding: 0,
  },
  infoLabel: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  buttonRed: {
    color: red[500],
    '&:hover': {
      backgroundColor: alpha(red[500], 0.04),
    },
  },
  buttonContainedRed: {
    color: theme?.custom?.button?.delete?.contrastText,
    backgroundColor: theme?.custom?.button?.delete?.main,
    marginLeft: theme.spacing(1), // space between fields
    '&:hover': {
      backgroundColor: theme?.custom?.button?.delete?.dark,
    },
  },
  dialogTitle: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dialogActions: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  dialog2: ({ customWidth }: any) => ({
    position: 'absolute',
    top: '1%',
    overflowY: 'initial',
    minWidth: customWidth,
    '& .MuiDialogContent-root': {
      overflowY: 'initial',
    },
  }),
  dialog: ({ customWidth }: any) => ({
    backgroundColor:
      theme.palette.mode === 'dark' ? '#121c21 !important' : '#fff',
    color: theme.palette.mode === 'dark' ? '#fff !important' : '#000',
    position: 'absolute',
    top: '1%',
    minWidth: customWidth,
  }),
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));
export interface IModalWithStyles {
  /** CSS override for modal component */
  className?: string;
  /** Title for modal */
  title: string;
  /** Specify customWidth in pixels for the dialog, customWidth overrides size property*/
  customWidth?: string;
  /** Show modal */
  show: boolean;
  /** Body of modal */
  children: ReactNode;
  /** Show informational on far left */
  infoLabel?: string;
  /** Hide modal */
  onHide: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => void;
  /** Hides button when empty */
  cancelLabel?: string;
  /** Submit callback */
  onSubmit?: (...props: any[]) => void;
  /** Hides button when empty */
  submitLabel?: string;
  /** Currently loading */
  isLoading?: boolean;
  /** Currenlty submitting */
  isSubmitting?: boolean;
  /** True to disable submit */
  disableSubmit?: boolean;
  /** Footer component */
  footerComponent?: ReactNode;
  /** size of modal */
  size?: 'xl' | 'lg' | 'sm' | 'xs' | 'md' | undefined;
  /** No padding for dialog */
  noPaddingDialog?: boolean;
  /** True to make button on footer say delete and be red */
  deleteDialog?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  dataPup?: string;
  nonScroll?: boolean;
}

/**
 * Standard modal with cancel and save buttons
 */
export const ModalWithStyles = ({
  className,
  title,
  show,
  children,
  onHide: onHideSource,
  onSubmit,
  infoLabel,
  cancelLabel = 'Cancel',
  submitLabel = 'Save',
  disableSubmit,
  isLoading = false,
  isSubmitting = false,
  footerComponent,
  size = 'md',
  customWidth = '',
  noPaddingDialog,
  deleteDialog,
  hideHeader = false,
  hideFooter = false,
  dataPup = undefined,
  nonScroll = false,
}: IModalWithStyles) => {
  const onHide: IModalWithStyles['onHide'] = (event, reason) => {
    if (reason !== 'backdropClick') {
      onHideSource?.(event, reason);
    }
  };
  const classes = useStyles({ customWidth });
  // const userAtom = useRecoilValueLoadable<any>(user);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { accountReadOnly } = permissionsLoadable.contents as IPermissions;

  return (
    <Dialog
      open={show}
      aria-labelledby="form-dialog-title"
      maxWidth={size}
      fullWidth={true}
      onClose={onHide}
      // disableBackdropClick={true}
      classes={{ paper: nonScroll ? classes.dialog2 : classes.dialog }}
      className={className}
    >
      {!hideHeader && (
        <DialogTitle className={classes.dialogTitle}>
          <Typography>{title}</Typography>
          {onHide ? (
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={onHide}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
      )}
      <DialogContent
        className={clsx({ [classes.dialogContent]: noPaddingDialog })}
      >
        {children}
        <Backdrop className={classes.backdrop} open={isLoading || isSubmitting}>
          <CircularProgress color="primary" />
        </Backdrop>
      </DialogContent>
      {!hideFooter && (
        <DialogActions className={classes.dialogActions}>
          {footerComponent ?? (
            <>
              {Boolean(infoLabel) && (
                <Typography className={classes.infoLabel}>
                  {infoLabel}
                </Typography>
              )}
              {Boolean(cancelLabel) && (
                <Button
                  className={classes.button}
                  disabled={isSubmitting}
                  variant="contained"
                  color="inherit"
                  disableElevation={true}
                  onClick={onHide}
                >
                  {cancelLabel}
                </Button>
              )}
              {Boolean(submitLabel) && (
                <Button
                  data-pup={dataPup ?? undefined}
                  color="primary"
                  className={clsx(classes.button, {
                    [classes.buttonContainedRed]: deleteDialog,
                  })}
                  variant="contained"
                  disableElevation={true}
                  disabled={isSubmitting || disableSubmit || !!accountReadOnly}
                  onClick={onSubmit}
                >
                  {deleteDialog ? 'Delete' : submitLabel}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};
